import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import '../ProductFilterPanel.scss';
import { removeDomainName, wishListHandlerHelper } from '../../../helper';
import { useDispatch, useSelector } from 'react-redux';
import { getProductPrice, getMarketPrice } from '../../../helper';
import { setToastMessage, shortlist } from '../../../../actionCreators/common';
import { isEmptyObject } from '../../../../utils/helper';
import { withRouter } from 'react-router';

const BASE_IMG_URLS = 'https://static.lenskart.com/owndays/img/plp';

const mapProductData = rawProduct => {
  const colorOptions = rawProduct.color_options || [];
  const colorsToShow = colorOptions.slice(0, 5);
  const extraColors = colorOptions.length > 5 ? colorOptions.length - 5 : 0;
  const colorThumbnails = colorsToShow.map(option => ({
    type: option.frameColorImage ? 'image' : 'color',
    value: option.frameColorImage || option.color,
    product_url: option.product_url,
  }));
  const isKidsLabel = !!rawProduct.customTags?.some(tag => tag.label === 'Kids');
  const sortingTagImage = rawProduct.sortingTag?.imageUrl || '';
  const price = getProductPrice(rawProduct);
  const marketPrice = getMarketPrice(rawProduct);
  const fallbackImages =
    Array.isArray(rawProduct.imageUrls) && rawProduct.imageUrls.length > 0
      ? rawProduct.imageUrls.map(img => img.image_url)
      : [];

  const modelNumber = rawProduct.model_name || '';
  const sku = rawProduct.colorCode || '';
  const classification = rawProduct.classification;
  const lensPerBox =
    classification === 'contact_lens'
      ? rawProduct?.hashtagList?.find(({ property }) => property === 'contact_packaging')?.name ||
        ''
      : '';

  const modelWithSku =
    classification === 'contact_lens'
      ? `${lensPerBox ? `| ${lensPerBox} Lenses/Box` : ''} ${sku}`
      : `${modelNumber} ${sku}`;

  return {
    id: rawProduct.id,
    name: rawProduct.brand_name_en || '',
    price,
    marketPrice,
    colorThumbnails,
    colorOptions,
    extraColors,
    finalImages: fallbackImages,
    imageUrls: rawProduct.imageUrls || [],
    hoverImage: rawProduct.hover_image_url || null,
    tagline: rawProduct.type === 'Eyeglasses' ? 'Lenses included' : null,
    productUrl: rawProduct.product_url || '',
    glassOnUrl: rawProduct.glassOnUrl || '',
    sortingTagImage,
    isBestSeller: sortingTagImage.includes('Icons@3x'),
    isNewArrivals: sortingTagImage.includes('New@3x'),
    isKidsLabel,
    modelWithSku,
  };
};

const ProductCard = ({ product, productSelectCallback, indexOfProduct, glassOnData }) => {
  const [hovered, setHovered] = useState(false);
  const [activeColorIndex, setActiveColorIndex] = useState(0);
  const [slMessage, setSlMessage] = useState({});
  const [isWishlistedLocal, setIsWishlistedLocal] = useState(false);

  const shortListData = useSelector(state => state.common.shortListData);
  const wishlistCount = useSelector(state => state.common.isWishlistCount);
  const login = useSelector(state => state.common.login);
  const dataLocale = useSelector(state => state.locale);
  const userInfo = useSelector(state => state.common.userInfo);
  const categoryData = useSelector(state => state.category?.categoryData?.result);
  const dispatch = useDispatch();

  const { CURRENCY_SYMBOL, CURRENCY_CODE, LENS_PER_BOX } = dataLocale;

  const sliderSettings = {
    dots: true,
    arrows: false,
    infinite: true,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    className: 'product-card__slider',
    swipe: true,
    autoplay: false,
    beforeChange: () => setHovered(false),
  };

  const currentColor = product.colorOptions[activeColorIndex] || {};
  const getCurrentImages = () => {
    if (
      product.colorOptions.length > 0 &&
      Array.isArray(currentColor.image_urls) &&
      currentColor.image_urls.length > 0
    ) {
      return currentColor.image_urls;
    }

    if (Array.isArray(product.imageUrls) && product.imageUrls.length > 0) {
      return product.imageUrls;
    }

    return ['https://static.lenskart.com/media/owndays/desktop/img/od-misc/no-image.jpg'];
  };

  const currentImages = getCurrentImages();

  const handleColorClick = index => {
    setActiveColorIndex(index);
  };

  useEffect(() => {
    const shortListProduct =
      shortListData?.result?.productIds.concat(
        shortListData.result.productList.map(({ id }) => id)
      ) || [];

    setIsWishlistedLocal(shortListProduct.includes(product?.id));
  }, [shortListData, product?.id]);

  useEffect(() => {
    if (!isEmptyObject(slMessage) && wishlistCount !== 50) {
      dispatch(setToastMessage(slMessage));
    }
  }, [dispatch, slMessage, wishlistCount]);

  const wishListHandler = event => {
    event.stopPropagation();

    setIsWishlistedLocal(prev => !prev);

    wishListHandlerHelper(
      event,
      product,
      wishlistCount,
      dataLocale,
      login,
      dispatch,
      setToastMessage,
      shortlist,
      setSlMessage,
      shortListData,
      userInfo,
      categoryData,
      indexOfProduct
    );
  };

  const handleProductClick = url => {
    if (productSelectCallback) {
      productSelectCallback(indexOfProduct);
    }

    const viewedProducts = localStorage.getItem('recentlyViewedProducts')
      ? JSON.parse(localStorage.getItem('recentlyViewedProducts'))
      : [];

    if (viewedProducts.length >= 8) {
      viewedProducts.pop();
    }

    const remainingProducts = viewedProducts.filter(obj => obj.id !== product?.id);
    const finalProducts = [product, ...remainingProducts];
    localStorage.setItem('recentlyViewedProducts', JSON.stringify(finalProducts));

    if (url?.length > 0 && url.includes('sg/en/')) {
      window.open(`/${removeDomainName(url)}`, '_self');
    } else {
      window.open(`/sg/en/${removeDomainName(url)}`, '_self');
    }
  };

  return (
    <div className="product-card">
      <div className="product-card__top-section">
        <div className="product-card__top-section-tag">
          {(product.isBestSeller || product.isNewArrivals) && (
            <img
              alt="Tag"
              className={`product-card__top-section-tag-icon ${
                product.isNewArrivals ? 'product-card__top-section-tag-icon-new' : ''
              }`}
              src={product.sortingTagImage}
            />
          )}
          {product.isBestSeller && (
            <span className="product-card__top-section-tag-text">BESTSELLER</span>
          )}
        </div>

        <div className="product-card__top-section-badge-group">
          {product.glassOnUrl && (
            <img
              alt="AR Icon"
              className="product-card__top-section-badge-group-ar-btn"
              src={`${BASE_IMG_URLS}/ar-icon.svg`}
              onClick={() => glassOnData?.(product.glassOnUrl)}
            />
          )}
          <img
            alt="Wishlist"
            className="product-card__top-section-badge-group-wishlist-btn"
            src={
              isWishlistedLocal
                ? `${BASE_IMG_URLS}/wishlist-icon-added.svg`
                : `${BASE_IMG_URLS}/wishlist-icon.svg`
            }
            onClick={wishListHandler}
          />
        </div>
      </div>

      <div
        className="product-card__image-section"
        onClick={e => {
          if (e.target.closest('.slick-dots') || e.target.closest('.slick-arrow')) {
            return;
          }

          handleProductClick(product.productUrl);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Slider {...sliderSettings}>
          {currentImages.map((url, index) => (
            <div key={index}>
              <img
                alt={`${product.name} - ${index}`}
                className="product-card__image-section-img"
                src={hovered && product.hoverImage ? product.hoverImage : url}
              />
            </div>
          ))}
        </Slider>
        {product.isKidsLabel && (
          <span
            className={`product-card__image-section-badge ${
              !product.isBestSeller ? 'product-card__image-section-badge--not-best' : ''
            }`}
          >
            KIDS
          </span>
        )}
      </div>

      <div className="product-card__divider" />
      <div className="product-card__color-swatches">
        {product.colorThumbnails.map((colorObj, index) => (
          <div
            key={index}
            className="product-card__color-swatches-warper"
            onClick={event => {
              event.stopPropagation();
              handleProductClick(colorObj?.product_url);
            }}
            onMouseEnter={() => handleColorClick(index)}
            onMouseLeave={() => setActiveColorIndex(0)}
          >
            <div
              className={`product-card__color-swatches-item ${
                activeColorIndex === index ? 'active' : ''
              }`}
            >
              <span className="product-card__color-swatches-swatch">
                {colorObj.type === 'image' ? (
                  <img
                    alt={`color-${index}`}
                    className="swatch-image swatch-image-src"
                    src={colorObj.value}
                  />
                ) : (
                  <span className="swatch-image" style={{ backgroundColor: colorObj.value }} />
                )}
              </span>
            </div>
          </div>
        ))}

        {product.extraColors > 0 && (
          <span className="product-card__color-swatches-extra-count">+{product.extraColors}</span>
        )}
      </div>

      <div className="product-card__info">
        <div className="product-card__info-row-t">
          <div className="product-card__info-name">{product.name}</div>
          {product.price < product.marketPrice ? (
            <span className="product-card__info__price-original">{`${CURRENCY_CODE}${CURRENCY_SYMBOL}${product.marketPrice}`}</span>
          ) : null}
        </div>
        <div className="product-card__info-row-bt">
          <div className="product-card__info-code">{product.modelWithSku}</div>
          <span className="product-card__info__price-discount">{`${CURRENCY_CODE}${CURRENCY_SYMBOL}${product.price}`}</span>
        </div>
        {product.tagline ? (
          <div className="product-card__info-tagline">{product.tagline}</div>
        ) : (
          <div></div>
        )}
      </div>
    </div>
  );
};

const ProductGrid = ({ product, productSelectCallback, indexOfProduct, glassOnData }) => {
  const mappedProduct = mapProductData(product);

  return (
    <ProductCard
      glassOnData={glassOnData}
      indexOfProduct={indexOfProduct}
      product={mappedProduct}
      productSelectCallback={productSelectCallback}
    />
  );
};

export default withRouter(ProductGrid);
